import type { AgentTool } from './tools';
import { forComponent } from './logging';

/**
 * Web tools — `WebFetch` (retrieve a URL as readable text) and `WebSearch` (ranked
 * results via a configured provider). Opt-in (NOT in the default tool set): network
 * access is a deliberate capability. Factory-built with an injectable `fetch` so they
 * stay edge-portable and unit-testable without real network. `fetch` is read at call
 * time, so a no-network runtime simply has the tool return an error.
 */
const log = forComponent('web');

/** Strip HTML to readable text — dependency-free: drop script/style/comments, block tags → newlines, decode common entities. */
export function htmlToText(html: string): string {
  let s = html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<title[\s\S]*?<\/title>/gi, ' ')       // drop title text (don't leak it into context)
    .replace(/<noscript[\s\S]*?<\/noscript>/gi, ' ') // …same for noscript / textarea content
    .replace(/<textarea[\s\S]*?<\/textarea>/gi, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<\/(p|div|li|h[1-6]|tr|section|article|header|footer|nav)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, ' ');
  s = s
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#0?39;/g, "'").replace(/&#x27;/gi, "'");
  return s
    .replace(/[ \t\f\v]+/g, ' ')
    .split('\n').map((l) => l.trim()).join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export interface WebFetchOptions {
  /** Override the global fetch (tests inject a mock; edge runtimes can supply their own). */
  fetch?: typeof globalThis.fetch;
  maxBytes?: number; // cap the downloaded body (default 2 MB)
  maxChars?: number; // cap the returned text (default 100k)
  timeoutMs?: number; // request timeout (default 15s)
  /** Allow fetching private/loopback/link-local hosts (default false — blocks basic SSRF). */
  allowPrivateHosts?: boolean;
}

/**
 * Block obvious SSRF targets by hostname/IP literal (loopback, private ranges, link-local incl.
 * cloud metadata 169.254.169.254, `.internal`). Pure/edge-safe — no DNS, so DNS-rebinding and
 * redirect-to-internal are NOT covered (an embedder needing that should supply a vetting `fetch`).
 */
export function isPrivateHost(host: string): boolean {
  const h = host.toLowerCase().replace(/^\[|\]$/g, ''); // strip IPv6 brackets
  if (h === '' || h === 'localhost' || h.endsWith('.localhost') || h.endsWith('.internal')) return true;
  if (h === '::1' || h === '::' || h.startsWith('fe80:') || h.startsWith('fc') || h.startsWith('fd')) return true; // IPv6 loopback/link-local/ULA
  const m = h.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (m) {
    const a = +m[1], b = +m[2];
    return a === 0 || a === 127 || a === 10 || (a === 169 && b === 254) || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) || (a === 100 && b >= 64 && b <= 127);
  }
  return false;
}

/** Lazily-loaded node DNS resolver (absent on edge/browser) — closes DNS-rebinding (a public
 *  hostname resolving to an internal IP) on the real-network path. Resolves null where unavailable. */
let _dnsLookup: ((h: string, opts?: any) => Promise<{ address: string }[]>) | null | undefined;
async function resolveIps(host: string): Promise<string[] | null> {
  if (_dnsLookup === undefined) {
    try { _dnsLookup = (await import('node:dns/promises')).lookup as any; }
    catch { _dnsLookup = null; } // edge/browser: no DNS — rely on the literal isPrivateHost check
  }
  if (!_dnsLookup) return null;
  try { return (await _dnsLookup(host, { all: true } as any)).map((a) => a.address); } catch { return null; }
}

/** Read a response body but stop at `maxBytes` of ACTUAL bytes (cancel the stream) — no unbounded download. */
async function readCapped(res: Response, maxBytes: number): Promise<string> {
  const reader = (res.body as any)?.getReader?.();
  if (!reader) { const t = await res.text(); return t.length > maxBytes ? t.slice(0, maxBytes) : t; }
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) { chunks.push(value); total += value.length; }
    if (total >= maxBytes) { try { await reader.cancel(); } catch { /* already closed */ } break; }
  }
  const out = new Uint8Array(Math.min(total, maxBytes));
  let off = 0;
  for (const c of chunks) { if (off >= out.length) break; const take = Math.min(c.length, out.length - off); out.set(c.subarray(0, take), off); off += take; }
  return new TextDecoder().decode(out);
}

/** Build a WebFetch tool. */
export function makeWebFetchTool(options: WebFetchOptions = {}): AgentTool {
  const maxBytes = options.maxBytes ?? 2_000_000;
  const maxChars = options.maxChars ?? 100_000;
  const timeoutMs = options.timeoutMs ?? 15_000;
  return {
    name: 'WebFetch',
    description:
      'Fetch an http/https URL and return its readable text (HTML is stripped to text). Use to read docs or web pages. Returns the status line then up to ~100k chars of content.',
    // Declared so a transport above the loop sizes its deadline around this one (see AgentTool.maxDurationMs).
    maxDurationMs: timeoutMs,
    parameters: { type: 'object', required: ['url'], properties: { url: { type: 'string', description: 'absolute http(s) URL' } } },
    async run({ url }) {
      const doFetch = options.fetch ?? globalThis.fetch;
      const customFetch = !!options.fetch; // injected fetch (tests/edge) owns its own vetting → skip DNS
      const u = String(url ?? '');
      try { new URL(u); } catch { return `Error: invalid URL: ${u}`; }
      if (!doFetch) return 'Error: no network (fetch) available in this runtime';
      // Reject a host that's a private/internal IP literal, or (on the real-network path) a name that
      // RESOLVES to one — re-checked on EVERY redirect hop so an external page can't bounce us internal.
      const hostBlock = async (hostname: string): Promise<string | null> => {
        if (options.allowPrivateHosts) return null;
        if (isPrivateHost(hostname)) return hostname;
        if (!customFetch) { const ips = await resolveIps(hostname); if (ips) for (const ip of ips) if (isPrivateHost(ip)) return `${hostname} → ${ip}`; }
        return null;
      };
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), timeoutMs);
      try {
        let current = u;
        let res: Response;
        for (let hop = 0; ; hop++) {
          const pu = new URL(current);
          if (pu.protocol !== 'http:' && pu.protocol !== 'https:') return `Error: only http/https URLs are allowed (got "${pu.protocol}")`;
          const blocked = await hostBlock(pu.hostname);
          if (blocked) return `Error: refusing to fetch a private/internal address (${blocked}) — set allowPrivateHosts to override`;
          res = await doFetch(current, { signal: ctl.signal, redirect: 'manual', headers: { 'user-agent': 'agentx (+https://github.com/Livshitz/agentx)' } });
          if (res.status >= 300 && res.status < 400 && res.headers.get('location')) {
            if (hop >= 5) return `Error fetching ${u}: too many redirects`;
            current = new URL(res.headers.get('location')!, current).toString(); // re-validated at loop top
            continue;
          }
          break;
        }
        const type = res.headers.get('content-type') ?? '';
        const body = await readCapped(res, maxBytes);
        const text = /html/i.test(type) || /^\s*<(?:!doctype|html)/i.test(body) ? htmlToText(body) : body.trim();
        const capped = text.length > maxChars ? text.slice(0, maxChars) + `\n… [truncated at ${maxChars} chars]` : text;
        return `${res.status} ${res.statusText} · ${new URL(current).host}\n\n${capped}`;
      } catch (e: any) {
        log.debug(`WebFetch ${u} failed`, e);
        return `Error fetching ${u}: ${e?.name === 'AbortError' ? `timed out after ${timeoutMs}ms` : (e?.message ?? e)}`;
      } finally {
        clearTimeout(timer);
      }
    },
  };
}

export interface WebSearchOptions {
  fetch?: typeof globalThis.fetch;
  /** Provider: 'auto' (default) prefers Firecrawl, then Tavily (by key presence), else keyless DuckDuckGo.
   *  'anthropic' delegates to a cheap model with Anthropic's native server-side web_search. */
  provider?: 'auto' | 'tavily' | 'duckduckgo' | 'anthropic' | 'firecrawl';
  /** API key for Tavily (default: process.env.TAVILY_API_KEY). */
  apiKey?: string;
  /** Tavily endpoint override. */
  endpoint?: string;
  /** API key for Firecrawl (default: process.env.FIRECRAWL_API_KEY). */
  firecrawlApiKey?: string;
  /** Firecrawl search endpoint override. */
  firecrawlEndpoint?: string;
  maxResults?: number; // default 5
  timeoutMs?: number; // default 15s
  /** Tool name (default 'WebSearch'). Set to register a provider-pinned variant, e.g. 'WebSearchAnthropic'. */
  name?: string;
  /** Tool description override (default: the generic ranked-results blurb). */
  description?: string;
  /** Anthropic key for provider 'anthropic' (default: process.env.ANTHROPIC_API_KEY). */
  anthropicApiKey?: string;
  /** Model for provider 'anthropic' (default 'claude-haiku-4-5'). */
  model?: string;
}

interface SearchHit { title: string; url: string; snippet: string }

/** Decode a DuckDuckGo HTML result href: results are `//duckduckgo.com/l/?uddg=<encoded-target>` redirects. */
export function decodeDdgUrl(href: string): string {
  const m = href.match(/[?&]uddg=([^&]+)/);
  if (m) { try { return decodeURIComponent(m[1]); } catch { /* fall through */ } }
  return href.startsWith('//') ? 'https:' + href : href;
}

/** Parse DuckDuckGo's HTML results page into hits (title/url/snippet) — dependency-free, zips anchors to snippets in order. */
export function parseDdgHtml(html: string, max: number): SearchHit[] {
  const anchors = [...html.matchAll(/<a[^>]*class="[^"]*result__a[^"]*"[^>]*href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/g)];
  const snippets = [...html.matchAll(/<a[^>]*class="[^"]*result__snippet[^"]*"[^>]*>([\s\S]*?)<\/a>/g)].map((m) => htmlToText(m[1]));
  const hits: SearchHit[] = [];
  for (let i = 0; i < anchors.length && hits.length < max; i++) {
    const url = decodeDdgUrl(anchors[i][1]);
    try { if (isPrivateHost(new URL(url).hostname)) continue; } catch { continue; } // skip junk/internal redirects
    hits.push({ title: htmlToText(anchors[i][2]) || '(untitled)', url, snippet: snippets[i] ?? '' });
  }
  return hits;
}

function formatHits(hits: SearchHit[]): string {
  if (!hits.length) return '(no results)';
  return hits.map((r, i) => `${i + 1}. ${r.title}\n   ${r.url}\n   ${r.snippet.replace(/\s+/g, ' ').slice(0, 240)}`).join('\n\n');
}

/** Provider 'firecrawl': agent-oriented search API (POST /v1/search, Bearer auth). Maps Firecrawl's
 *  result objects into the same hit shape as Tavily/DDG. HTTP-only, no SDK. */
async function firecrawlSearch(q: string, opts: { key: string; endpoint: string; maxResults: number; fetch: typeof globalThis.fetch; signal: AbortSignal }): Promise<string> {
  const res = await opts.fetch(opts.endpoint, {
    method: 'POST',
    signal: opts.signal,
    headers: { authorization: `Bearer ${opts.key}`, 'content-type': 'application/json' },
    body: JSON.stringify({ query: q, limit: opts.maxResults }),
  });
  if (!res.ok) return `Error: Firecrawl search returned ${res.status} ${res.statusText}`;
  const data: any = await res.json();
  const results = Array.isArray(data?.data) ? data.data.slice(0, opts.maxResults) : [];
  return formatHits(results.map((r: any) => ({ title: r.title ?? '(untitled)', url: r.url ?? '', snippet: String(r.description ?? r.markdown ?? '') })));
}

/**
 * Provider 'anthropic': delegate the search to a cheap model running Anthropic's native server-side
 * web_search tool. Anthropic runs the search and reads the results; we prompt for un-editorialized
 * findings (one claim per line, each with its source URL) and relay them verbatim. Raw HTTP — no SDK,
 * stays edge-portable. Slower and costlier than DDG/Tavily, but better-sourced (native search index).
 */
async function anthropicSearch(q: string, opts: { key: string; model: string; fetch: typeof globalThis.fetch; signal: AbortSignal }): Promise<string> {
  const res = await opts.fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    signal: opts.signal,
    headers: { 'x-api-key': opts.key, 'anthropic-version': '2023-06-01', 'content-type': 'application/json' },
    body: JSON.stringify({
      model: opts.model,
      max_tokens: 1024,
      // Basic variant: Haiku-tier doesn't support the _20260209 dynamic-filtering variant (Opus 4.6+/Sonnet 4.6 only).
      tools: [{ type: 'web_search_20250305', name: 'web_search', max_uses: 5 }],
      messages: [{ role: 'user', content:
        `Search the web for: ${q}\n\nReturn only the relevant findings as concise bullet points, each with its source URL in parentheses. ` +
        `Do not add a preamble, conclusion, opinion, or commentary. If sources conflict, list each claim with its source rather than resolving it.` }],
    }),
  });
  if (!res.ok) return `Error: Anthropic search returned ${res.status} ${res.statusText}`;
  const data: any = await res.json();
  if (data?.stop_reason === 'refusal') return 'Error: Anthropic search refused the query';
  let text = '';
  for (const block of data?.content ?? []) if (block?.type === 'text') text += block.text;
  return text.trim() || '(no results)';
}

/**
 * Build a WebSearch tool. Keyless by default (DuckDuckGo HTML) so it works in any deployment with no
 * setup; on provider 'auto' it upgrades to an agent-oriented API by key presence — Firecrawl
 * (FIRECRAWL_API_KEY) preferred, then Tavily (TAVILY_API_KEY). Provider 'anthropic' is a separate path
 * (native search via a cheap model) exposed as the WebSearchAnthropic tool.
 */
export function makeWebSearchTool(options: WebSearchOptions = {}): AgentTool {
  const tavilyEndpoint = options.endpoint ?? 'https://api.tavily.com/search';
  const firecrawlEndpoint = options.firecrawlEndpoint ?? 'https://api.firecrawl.dev/v1/search';
  const maxResults = options.maxResults ?? 5;
  const timeoutMs = options.timeoutMs ?? 15_000;
  return {
    name: options.name ?? 'WebSearch',
    description: options.description ??
      'Search the web by query; returns ranked results (title, URL, snippet). Use to look things up, find pages, or research a topic — then WebFetch a result URL to read it in full.',
    parameters: { type: 'object', required: ['query'], properties: { query: { type: 'string' } } },
    async run({ query }) {
      const doFetch = options.fetch ?? globalThis.fetch;
      if (!doFetch) return 'Error: no network (fetch) available in this runtime';
      const q = String(query ?? '').trim();
      if (!q) return 'Error: empty query';
      const key = options.apiKey ?? process.env.TAVILY_API_KEY;
      const fcKey = options.firecrawlApiKey ?? process.env.FIRECRAWL_API_KEY;
      const provider = options.provider ?? 'auto';
      // auto priority ladder: Firecrawl > Tavily > keyless DuckDuckGo (by key presence).
      const useFirecrawl = provider === 'firecrawl' || (provider === 'auto' && !!fcKey);
      const useTavily = provider === 'tavily' || (provider === 'auto' && !useFirecrawl && !!key);
      const ctl = new AbortController();
      const timer = setTimeout(() => ctl.abort(), timeoutMs);
      try {
        if (provider === 'anthropic') {
          const akey = options.anthropicApiKey ?? process.env.ANTHROPIC_API_KEY;
          if (!akey) return 'Error: WebSearchAnthropic requires ANTHROPIC_API_KEY (set in env)';
          return await anthropicSearch(q, { key: akey, model: options.model ?? 'claude-haiku-4-5', fetch: doFetch, signal: ctl.signal });
        }
        if (useFirecrawl) {
          if (!fcKey) return 'Error: Firecrawl provider selected but FIRECRAWL_API_KEY is not set';
          return await firecrawlSearch(q, { key: fcKey, endpoint: firecrawlEndpoint, maxResults, fetch: doFetch, signal: ctl.signal });
        }
        if (useTavily) {
          if (!key) return 'Error: Tavily provider selected but TAVILY_API_KEY is not set';
          const res = await doFetch(tavilyEndpoint, {
            method: 'POST',
            signal: ctl.signal,
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ api_key: key, query: q, max_results: maxResults }),
          });
          if (!res.ok) return `Error: search provider returned ${res.status} ${res.statusText}`;
          const data: any = await res.json();
          const results = Array.isArray(data?.results) ? data.results.slice(0, maxResults) : [];
          return formatHits(results.map((r: any) => ({ title: r.title ?? '(untitled)', url: r.url ?? '', snippet: String(r.content ?? '') })));
        }
        // Keyless: DuckDuckGo HTML endpoint (no key, edge-portable).
        const res = await doFetch('https://html.duckduckgo.com/html/?q=' + encodeURIComponent(q), {
          signal: ctl.signal,
          headers: { 'user-agent': 'Mozilla/5.0 (compatible; agentx/1.0; +https://github.com/Livshitz/agentx)' },
        });
        if (!res.ok) return `Error: search returned ${res.status} ${res.statusText}`;
        return formatHits(parseDdgHtml(await res.text(), maxResults));
      } catch (e: any) {
        log.debug('WebSearch failed', e);
        return `Error searching: ${e?.name === 'AbortError' ? `timed out after ${timeoutMs}ms` : (e?.message ?? e)}`;
      } finally {
        clearTimeout(timer);
      }
    },
  };
}

/** Default instances (registered in the tool registry; opt-in by name). */
export const webFetchTool = makeWebFetchTool();
export const webSearchTool = makeWebSearchTool();
const ANTHROPIC_SEARCH_DESC =
  "High-quality web search via Anthropic's native search index. Returns concise, sourced findings — one claim per line, each with its source URL. Prefer this over WebSearch when accuracy and citations matter; it is slower (~3–8s) and bills the Anthropic API account per call (one cheap-model turn + search fee).";

/** Anthropic-native search as a provider-pinned WebSearch variant. The credential is API-key billing
 *  (x-api-key); pass `anthropicApiKey` to inject a resolved key, else it falls back to ANTHROPIC_API_KEY. */
export function makeWebSearchAnthropicTool(opts: { anthropicApiKey?: string; model?: string } = {}): AgentTool {
  return makeWebSearchTool({ provider: 'anthropic', name: 'WebSearchAnthropic', description: ANTHROPIC_SEARCH_DESC, anthropicApiKey: opts.anthropicApiKey, model: opts.model });
}
/** Env-keyed singleton: registered in the tool registry so 'WebSearchAnthropic' resolves by name. The
 *  CLI default-on path builds its own instance with a config-resolved key (see cli/core.ts). */
export const webSearchAnthropicTool = makeWebSearchAnthropicTool();
